// Import the parse function from csv-parse/browser instead of csv-parse/sync
// This is a browser-compatible version that doesn't require the Node.js Buffer
import { parse } from 'csv-parse/browser/esm/sync';
import { GlassType } from '../types/glassTypes';
import { v4 as uuidv4 } from 'uuid';

// Improved browser-compatible CSV parser that handles problematic quotes
const parseCSVFallback = (csvString: string, options: any = {}): any[] => {
  const { columns = true, skip_empty_lines = true, trim = true } = options;

  // Normalize line endings and split into lines
  const lines = csvString.replace(/\r\n/g, '\n').split('\n');
  if (lines.length === 0) return [];

  // Parse header row
  const headerLine = lines[0];
  const headers = parseCSVLine(headerLine, trim);

  // Parse data rows
  const results: any[] = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (skip_empty_lines && (!line || line.trim() === '')) continue;

    const values = parseCSVLine(line, trim);

    // Create object from headers and values
    if (columns) {
      const row: any = {};
      for (let j = 0; j < headers.length; j++) {
        if (j < values.length) {
          row[headers[j]] = values[j];
        }
      }
      results.push(row);
    } else {
      results.push(values);
    }
  }

  return results;
};

// Helper function to parse a single CSV line, handling quotes properly
const parseCSVLine = (line: string, trim = true): string[] => {
  const values: string[] = [];
  let currentValue = '';
  let inQuotes = false;
  let i = 0;

  // Handle special case for CLG data with problematic quotes
  // Pre-process the line to fix common quote issues
  line = line
    // Fix "" pattern that should be "
    .replace(/"""/g, '""')
    // Fix missing closing quotes at the end of fields
    .replace(/([^,"]+)"(,|$)/g, '"$1"$2');

  while (i < line.length) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
        // Handle escaped quotes ("")
        currentValue += '"';
        i++; // Skip the next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // End of field
      values.push(trim ? currentValue.trim() : currentValue);
      currentValue = '';
    } else {
      // Regular character
      currentValue += char;
    }

    i++;
  }

  // Add the last value
  values.push(trim ? currentValue.trim() : currentValue);

  return values;
};

/**
 * Normalizes glass type names to standard categories
 * @param type Raw glass type string
 * @returns Normalized glass type string
 */
const normalizeGlassType = (type: string): string => {
  // Convert to lowercase for case-insensitive comparison
  const typeLower = type.toLowerCase().trim();

  // Check if it's a numeric value (like weight) mistakenly put in the type field
  if (!isNaN(parseFloat(typeLower))) {
    // If it's a number, try to determine the type based on the value
    const value = parseFloat(typeLower);
    if (value >= 6.0 && value <= 7.0) return 'IG Commercial'; // Changed from 'Insulated' to 'IG Commercial'
    if (value >= 3.0 && value <= 3.5) return 'Monolithic';
    if (value >= 1.5 && value <= 2.0) return 'Monolithic';
    if (value < 1.5) return 'Monolithic';

    // Default to Specialty if we can't determine
    return 'Specialty';
  }

  // Map common variations to standard categories
  // Use more specific checks first, then more general ones

  // Check for exact matches first (case-insensitive)
  const exactMatches = {
    'mirror': 'Mirror',
    'monolithic': 'Monolithic',
    'laminated': 'Laminated',
    'wire': 'Wire',
    'fire rated': 'Fire Rated',
    'pattern': 'Pattern',
    'spandrel': 'Spandrel',
    'specialty': 'Specialty',
    'ig commercial': 'IG Commercial',
    'ig residential': 'IG Residential',
    'insulated': 'IG Commercial' // Map 'insulated' to 'IG Commercial'
  };

  if (exactMatches[typeLower]) {
    return exactMatches[typeLower];
  }

  // Then check for partial matches
  if (typeLower.includes('mirror')) return 'Mirror';
  if (typeLower.includes('mono')) return 'Monolithic';
  if (typeLower.includes('lami')) return 'Laminated';
  // Map 'insulated' and 'ig' to 'IG Commercial' instead of 'Insulated'
  if (typeLower.includes('insul')) return 'IG Commercial';
  if (typeLower.includes('ig')) return 'IG Commercial';
  if (typeLower.includes('wire')) return 'Wire';
  if (typeLower.includes('fire')) return 'Fire Rated';
  if (typeLower.includes('pattern')) return 'Pattern';
  if (typeLower.includes('spandrel')) return 'Spandrel';
  if (typeLower.includes('special')) return 'Specialty';

  // If it's already a standard category, capitalize first letter
  const standardCategories = [
    'mirror', 'monolithic', 'laminated', 'wire', 'fire rated',
    'pattern', 'spandrel', 'specialty', 'ig commercial', 'ig residential'
  ];
  if (standardCategories.includes(typeLower)) {
    // Special case for IG types to ensure proper capitalization
    if (typeLower.startsWith('ig ')) {
      return 'IG ' + typeLower.charAt(3).toUpperCase() + typeLower.slice(4);
    }
    return typeLower.charAt(0).toUpperCase() + typeLower.slice(1);
  }

  // If we can't determine, return the original with first letter capitalized
  return type.charAt(0).toUpperCase() + type.slice(1);
};

/**
 * Converts a thickness string to a numeric value
 * @param thickness Thickness string (e.g., "1/4\"", "3/8\"", "1\"")
 * @returns Numeric thickness value in inches
 */
export const convertThicknessToNumber = (thickness: string): number => {
  if (!thickness) return 0;

  // Remove any quotes and trim whitespace
  const cleanThickness = thickness.replace(/"/g, '').trim();

  // Special cases
  if (cleanThickness === 'SSB') return 0.125; // Single Strength B
  if (cleanThickness === 'Tempered') return 0.25;
  if (cleanThickness === 'Annealed') return 0.25;

  // Handle fractions like "1/4"
  if (cleanThickness.includes('/')) {
    const parts = cleanThickness.split('/');
    if (parts.length === 2) {
      const numerator = parseFloat(parts[0]);
      const denominator = parseFloat(parts[1]);
      if (!isNaN(numerator) && !isNaN(denominator) && denominator !== 0) {
        return numerator / denominator;
      }
    }
  }

  // Handle simple numbers
  const numValue = parseFloat(cleanThickness);
  if (!isNaN(numValue)) {
    return numValue;
  }

  // Default fallback
  console.warn(`Could not parse thickness: ${thickness}`);
  return 0;
};

/**
 * Parses CSV data and converts it to GlassType objects
 * @param csvData Raw CSV data as a string
 * @param source Source identifier (e.g., 'CLG', 'OldCastle')
 * @returns Array of GlassType objects
 */
export const parseCSVToGlassTypes = (csvData: string, source: string): GlassType[] => {
  try {
    // Try to parse CSV data with the imported parser first
    let records;
    try {
      records = parse(csvData, {
        columns: true,
        skip_empty_lines: true,
        trim: true
      });
    } catch (parseError) {
      console.warn('Error using csv-parse library, falling back to custom parser:', parseError);
      // Fall back to our custom parser if the imported one fails
      records = parseCSVFallback(csvData, {
        columns: true,
        skip_empty_lines: true,
        trim: true
      });
    }

    // Convert records to GlassType objects
    const result = records.map((record: any, index: number) => {
      try {
        // Extract name and clean it up
        let name = record.Name || '';
        let type = record.Type || '';
        let thickness = record.Thickness || '';

        // Handle case where the name contains the thickness
        if (name.includes('¼') || name.includes('1/4') || name.includes('3/8') ||
            name.includes('1/2') || name.includes('3/16') || name.includes('1/8')) {
          // Extract just the name part without the thickness
          name = name.replace(/[\d\/\"\s]+$/, '').trim();
        }

        // Normalize type to proper case
        type = normalizeGlassType(type);

        // More detailed debug logging for every 5th record to avoid console spam
        if (index % 5 === 0) {
          console.log(`Processing CSV record ${index}:`, {
            original: record,
            parsed: { name, type, thickness }
          });
        }

        return {
          id: uuidv4(),
          name: name,
          type: type,
          thickness: convertThicknessToNumber(thickness),
          thicknessStr: thickness, // Keep original thickness string
          lbsPerSqFt: parseFloat(record['Lbs/SqFt']) || 0,
          price: parseFloat(record.Price) || 0,
          source: source
        };
      } catch (error) {
        console.error(`Error processing record ${index}:`, record, error);
        // Return a default record to avoid breaking the whole dataset
        return {
          id: uuidv4(),
          name: record.Name || 'Unknown',
          type: 'Unknown',
          thickness: 0,
          thicknessStr: '',
          lbsPerSqFt: 0,
          price: 0,
          source: source
        };
      }
    });

    console.log(`Parsed ${result.length} records from CSV data`);
    if (result.length > 0) {
      console.log('Sample parsed record:', result[0]);
    }

    return result;
  } catch (error) {
    console.error('Error parsing CSV data:', error);
    return [];
  }
};

/**
 * Parses CSV content from a string
 * @param csvString CSV content as a string
 * @returns Array of objects representing the CSV data
 */
export const parseCSVString = (csvString: string): any[] => {
  try {
    return parse(csvString, {
      columns: true,
      skip_empty_lines: true,
      trim: true
    });
  } catch (error) {
    console.warn('Error using csv-parse library, falling back to custom parser:', error);
    return parseCSVFallback(csvString, {
      columns: true,
      skip_empty_lines: true,
      trim: true
    });
  }
};

/**
 * Fetches a CSV file and parses it into GlassType objects
 * @param url URL of the CSV file
 * @param source Source identifier (e.g., 'CLG', 'OldCastle')
 * @returns Promise resolving to an array of GlassType objects
 */
export const fetchAndParseCSV = async (url: string, source: string): Promise<GlassType[]> => {
  try {
    console.log(`Fetching CSV from ${url}`);
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch CSV: ${response.status} ${response.statusText}`);
    }

    const csvData = await response.text();
    console.log(`Successfully fetched CSV data from ${url}, first 100 chars:`, csvData.substring(0, 100));
    const result = parseCSVToGlassTypes(csvData, source);
    console.log(`Parsed ${result.length} glass types from ${url}`);
    return result;
  } catch (error) {
    console.error(`Error fetching CSV from ${url}:`, error);
    return [];
  }
};
